import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCastContext } from '../context/CastContext';
import { castApi } from '../services/castApi';
import ThemeSwitcher from '../../../basic/ThemeSwitcher';
import { useTheme } from '../../../basic/ThemeProvider';
import Typography from '../../../basic/Typography';
import { Agent } from '../types';
import styles from '../Cast.module.css';

export const AgentViewer: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { dispatch } = useCastContext();
  const { isDark, toggleTheme } = useTheme();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAgent = async () => {
      if (!agentId) return;

      setLoading(true);
      setError(null);

      try {
        const agentData = await castApi.getAgent(agentId);
        setAgent(agentData);
        dispatch({ type: 'SELECT_AGENT', payload: agentData });
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to load agent');
      } finally {
        setLoading(false);
      }
    };

    loadAgent();
  }, [agentId, dispatch]);

  const handleBack = () => {
    navigate('/cast');
  };

  if (loading) {
    return (
      <div className={styles['agent-viewer-loading']}>
        <div className={styles['loading-spinner']}></div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className={styles['agent-viewer-error']}>
        <div className={styles['error-message']}>
          <h3>Error Loading Agent</h3>
          <p>{error || 'Agent not found'}</p>
          <button className={styles['back-button']} onClick={handleBack}>
            Back to Agents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['agent-viewer']}>
      <div className={styles['viewer-header']}>
        <div className={styles['viewer-header-left']}>
          <button className={styles['back-button']} onClick={handleBack}>
            ← Back to Agents
          </button>
          <Typography variant="h2" className={styles['agent-title']} gutterBottom={false}>
            {agent.name}
          </Typography>
        </div>
        <div className={styles['viewer-header-right']}>
          <ThemeSwitcher isDark={isDark} onToggle={toggleTheme} />
        </div>
      </div>

      <div className={styles['agent-details']}>
        <div className={styles['agent-main-info']}>
          <div className={styles['agent-image-section']}>
            {agent.headshot_url ? (
              <img
                src={`/cast/${agent.headshot_url}`}
                alt={`${agent.name} headshot`}
                className={styles['agent-detail-headshot']}
              />
            ) : (
              <div className={styles['agent-detail-placeholder']}>
                <span className={styles['placeholder-icon']}>👤</span>
              </div>
            )}
          </div>

          <div className={styles['agent-info-section']}>
            <Typography variant="h3" gutterBottom={true}>
              About {agent.name}
            </Typography>
            {agent.bio ? (
              <Typography variant="body1" className={styles['agent-bio-full']} gutterBottom={false}>
                {agent.bio}
              </Typography>
            ) : (
              <Typography variant="body1" className={styles['no-bio']} gutterBottom={false}>
                No biography available
              </Typography>
            )}
          </div>
        </div>

        <div className={styles['agent-assets-section']}>
          <Typography variant="h3" gutterBottom={true}>
            Agent Assets
          </Typography>

          {agent.control_voices && agent.control_voices.length > 0 && (
            <div className={styles['asset-group']}>
              <Typography variant="h4" gutterBottom={true}>
                Voice Samples
              </Typography>
              <div className={styles['asset-list']}>
                {agent.control_voices.map((voice, index) => (
                  <div key={index} className={styles['asset-item']}>
                    <audio controls>
                      <source src={`/cast/${voice}`} type="audio/wav" />
                      Your browser does not support the audio tag.
                    </audio>
                  </div>
                ))}
              </div>
            </div>
          )}

          {agent.auditions && agent.auditions.length > 0 && (
            <div className={styles['asset-group']}>
              <Typography variant="h4" gutterBottom={true}>
                Auditions
              </Typography>
              <div className={styles['asset-list']}>
                {agent.auditions.map((audition, index) => (
                  <div key={index} className={styles['asset-item']}>
                    <video controls playsInline preload="metadata">
                      <source src={`/cast/${audition}#t=0.001`} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                ))}
              </div>
            </div>
          )}

          {agent.char_sheets && agent.char_sheets.length > 0 && (
            <div className={styles['asset-group']}>
              <Typography variant="h4" gutterBottom={true}>
                Character Sheets
              </Typography>
              <div className={styles['asset-list']}>
                {agent.char_sheets.map((sheet, index) => (
                  <div key={index} className={styles['asset-item']}>
                    <img src={`/cast/${sheet}`} alt={`Character sheet ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!agent.char_sheets || agent.char_sheets.length === 0) &&
           (!agent.auditions || agent.auditions.length === 0) &&
           (!agent.control_voices || agent.control_voices.length === 0) && (
            <Typography variant="body1" className={styles['no-assets']} gutterBottom={false}>
              No assets available for this agent
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};